<script setup>
import { computed, ref, watch } from "vue";
import { useModalogStore } from "@/stores/modalog.js";
import { useToastStore } from "@/stores/toast.js";
import { useConfetti } from "@/composables/useConfetti.js";
import BrandButton from "@/Components/Brand/BrandButton.vue";
import BrandModalogShell from "@/Components/Modalogs/BrandModalogShell.vue";
import IconTourExtension from "~/svg/tour-extension.svg";

/**
 * First-run onboarding tour (GH-1056, per Figma 333-134): five stepped
 * screens under the shared gradient header. The dashboard opens it once per
 * browser (localStorage) and the account dropdown relaunches it on demand.
 *
 * Step artwork loads from public/images/onboarding/tour-step-{n}.png and
 * falls back to a subdued logo placeholder until the exports land. The
 * extension step's art is the bare starburst; the glowing Install button
 * over it is live (BrandButton styled to the mock), not baked in.
 */
const modalogStore = useModalogStore();

const STEPS = [
    { key: "start", title: "Getting Started" },
    { key: "extension", title: "Install The Gift Hero Browser Extension" },
    { key: "dashboard", title: "The Dashboard" },
    { key: "lists", title: "Managing Your Lists & Profile" },
    { key: "inspiration", title: "Need Some Inspiration?" },
];

const step = ref(0);

// The modalog root stays mounted between opens (stable-root pattern), so a
// relaunch would otherwise resume on the last slide — rewind on reopen.
watch(
    () => modalogStore.hasModalog,
    (open) => {
        if (open) {
            step.value = 0;
        }
    },
);

const isFirst = computed(() => step.value === 0);
const isLast = computed(() => step.value === STEPS.length - 1);
const current = computed(() => STEPS[step.value]);

function next() {
    if (!isLast.value) {
        step.value += 1;
    }
}

function prev() {
    if (!isFirst.value) {
        step.value -= 1;
    }
}

function goTo(index) {
    step.value = index;
}

// Get Started! closes the tour with a little send-off: a brand-colour
// confetti burst over the dashboard and a toast. The toast store resolves
// lazily so mounting the modalog (and its tests) doesn't need it active.
const { burst } = useConfetti();

function finish() {
    modalogStore.closeModalog();
    burst();
    useToastStore().success("You\u2019re all set \u2014 happy gifting!");
}

// One flag per step so a missing export only hides its own image.
const imageFailed = ref(STEPS.map(() => false));

// Prev/next are primary BrandButtons (52px squares, 44px below lg); the
// first step's inert prev slot is a plain tan square so it can't wipe red.
const arrowClasses = "size-[52px] shrink-0 !p-0 max-lg:size-11";
</script>

<template>
    <!-- Root stays mounted; overlay toggles on an inner v-if (see the
         brand modalog pattern in ManageProfileThemeModalog). -->
    <div>
        <BrandModalogShell
            v-if="modalogStore.hasModalog"
            title="Welcome to Gift Hero!"
            max-width-class="max-w-[815px]"
        >
            <!-- Step artwork -->
            <div
                class="relative grid aspect-[745/341] w-full place-items-center overflow-hidden rounded-[5px] bg-brand-cream"
            >
                <img
                    v-if="!imageFailed[step]"
                    :src="asset(`images/onboarding/tour-step-${step + 1}.png`)"
                    alt=""
                    class="absolute inset-0 size-full object-cover"
                    @error="imageFailed[step] = true"
                />
                <img
                    v-else
                    :src="asset('images/brand/logo-mark.png')"
                    alt=""
                    aria-hidden="true"
                    class="size-14 opacity-25 grayscale"
                />
                <!-- Per the mock: navy pill with a 2px white keyline and the
                     warm glow, sitting on the starburst (max-w keeps it
                     inside the art at small widths). -->
                <BrandButton
                    v-if="current.key === 'extension'"
                    :href="route('browser-extensions')"
                    icon-rest-class="text-brand-red"
                    class="relative h-12 w-[295px] max-w-[80%] border-2 border-solid border-white text-[16px] shadow-[0_0_20px_5px_#fff8ac] hover:shadow-[0_0_20px_5px_#fff8ac]"
                >
                    <template #icon>
                        <IconTourExtension
                            class="h-4 w-[15px]"
                            aria-hidden="true"
                        />
                    </template>
                    Install Browser Extension
                </BrandButton>
            </div>

            <h3
                class="mt-7 text-center font-brand-display text-[33px] font-black italic uppercase leading-[0.9] text-[#102a3d] max-lg:mt-5 max-lg:text-[25px]"
            >
                {{ current.title }}
            </h3>

            <div
                class="mx-auto mt-3 max-w-[620px] text-center font-brand text-[20px] font-medium text-[#102a3d] max-lg:text-[15px] [&_p]:!leading-[1.4]"
            >
                <p v-if="current.key === 'start'">
                    <strong>Welcome to Gift Hero!</strong> We make creating and
                    sharing lists easier than ever. Lets go through a quick few
                    things you should know to make your time here effortless.
                </p>
                <p v-else-if="current.key === 'extension'">
                    Installing the Gift Hero browser extension on desktop and
                    mobile is pivotal to the list building experience. With the
                    browser extension, you&rsquo;ll be just a couple clicks away
                    from adding anything you find online to a wish list!
                    <IconTourExtension
                        class="mr-0.5 inline-block h-[16px] w-[15px] align-[-2px] text-brand-red"
                        aria-hidden="true"
                    />
                    <a
                        :href="route('browser-extensions')"
                        class="font-bold underline underline-offset-2 transition hover:text-brand-red"
                        >Install it now</a
                    >.
                </p>
                <p v-else-if="current.key === 'dashboard'">
                    The dashboard is your new home for all things trending and
                    new! See your most recent lists, our newest gift ideas,
                    friend updates and more!
                </p>
                <p v-else-if="current.key === 'lists'">
                    &lsquo;My Lists&rsquo; is your home for managing all your
                    lists and profile information in one spot. Better yet, you
                    can customize the look of your profile and lists to make
                    sure the experience is uniquely you!
                </p>
                <p v-else>
                    We want gift giving to be easier than ever with Gift Hero,
                    so we constantly curate our recommended list of top gifts
                    seasonally. Beyond that, we work with experts and
                    influencers to stay on trend so you know that you&rsquo;re
                    always gifting your best.
                </p>
            </div>

            <!-- Footer: arrows + dots, or Get Started! on the last step -->
            <footer class="mt-9 max-lg:mt-6">
                <div v-if="isLast" class="flex justify-center">
                    <BrandButton class="h-11 px-10 text-[15px]" @click="finish">
                        Get Started!
                    </BrandButton>
                </div>
                <div v-else class="flex items-center justify-between">
                    <BrandButton
                        v-if="!isFirst"
                        :class="arrowClasses"
                        aria-label="Previous step"
                        @click="prev"
                    >
                        <svg
                            viewBox="0 0 8 12"
                            class="h-3.5 w-2.5"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path d="M8 0v12L0 6z" />
                        </svg>
                    </BrandButton>
                    <span
                        v-else
                        :class="arrowClasses"
                        class="grid place-items-center rounded-brand bg-[#dfdbd7] text-white"
                        aria-hidden="true"
                    >
                        <svg
                            viewBox="0 0 8 12"
                            class="h-3.5 w-2.5"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path d="M8 0v12L0 6z" />
                        </svg>
                    </span>

                    <!-- Step markers double as jump links (11px squares on a
                         25px pitch per the mock). Fills are !important to
                         beat the brand reset on native buttons. -->
                    <div class="flex items-center gap-[14px]">
                        <button
                            v-for="(s, i) in STEPS"
                            :key="s.key"
                            type="button"
                            class="size-[11px] rounded-[2px] transition-colors"
                            :class="
                                i === step
                                    ? '!bg-brand-red'
                                    : '!bg-[#ebebeb] hover:!bg-[#d9d4d0]'
                            "
                            :aria-label="`Go to step ${i + 1}: ${s.title}`"
                            :aria-current="i === step ? 'step' : undefined"
                            @click="goTo(i)"
                        ></button>
                    </div>

                    <BrandButton
                        :class="arrowClasses"
                        aria-label="Next step"
                        @click="next"
                    >
                        <svg
                            viewBox="0 0 8 12"
                            class="h-3.5 w-2.5"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path d="M0 0v12l8-6z" />
                        </svg>
                    </BrandButton>
                </div>
            </footer>
        </BrandModalogShell>
    </div>
</template>
